import React from 'react';
import { CheckCircle2, Star } from 'lucide-react';
interface CompTier {
  units: string;
  bps: number;
  bonus?: number;
}
interface RoadmapTierProps {
  number: number;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  isActive?: boolean;
  isNext?: boolean;
  colorClass: string;
  bgHeaderClass: string;
  requirements: React.ReactNode;
  focusItems: string[];
  compTiers: CompTier[];
  goal: string;
  goalSub: string;
}
export function RoadmapTier({
  number,
  title,
  subtitle,
  icon,
  isActive,
  isNext,
  colorClass,
  bgHeaderClass,
  requirements,
  focusItems,
  compTiers,
  goal,
  goalSub
}: RoadmapTierProps) {
  return <div className={`relative flex flex-col bg-white rounded-xl border-2 ${isActive ? 'border-blue-500 shadow-lg shadow-blue-100 z-10' : isNext ? 'border-gray-300 shadow-md' : 'border-gray-200'} transition-all duration-300`} data-id="element-898" data-mp="2rnmdvqpw3f05">
      {isActive && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md z-20 whitespace-nowrap" data-id="element-899" data-mp="2dnblukcufude">
          You Are Here
        </div>}

      {isNext && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-700 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md z-20 whitespace-nowrap" data-id="element-900" data-mp="2jib8fnpnk39m">
          Next Target
        </div>}

      {/* Header */}
      <div className={`${bgHeaderClass} text-white p-4 pt-5 flex flex-col items-center text-center relative rounded-t-[10px] overflow-hidden`} data-id="element-901" data-mp="2xhklieikah9d">
        <div className="absolute top-1 left-3 text-4xl font-black opacity-20 leading-none pointer-events-none" data-id="element-902" data-mp="2ofusx5cy1ca0">
          {number}
        </div>
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-2 z-10" data-id="element-903" data-mp="2sflg6z39ha9s">
          {icon}
        </div>
        <h3 className="text-base font-extrabold tracking-tight leading-tight z-10 px-2" data-id="element-904" data-mp="1d2sbk830sios">
          {title}
        </h3>
        {subtitle && <p className="text-[10px] font-medium uppercase tracking-wider opacity-90 mt-1 z-10 leading-tight" data-id="element-905" data-mp="3jnn2equcihg2">
            {subtitle}
          </p>}
      </div>

      <div className="p-4 flex-1 flex flex-col" data-id="element-906" data-mp="39ylod67d2a4q">
        {/* Requirements */}
        <div className="mb-4" data-id="element-907" data-mp="39wio7tchv0ma">
          <div className="flex items-center justify-center mb-3" data-id="element-908" data-mp="350bs0s9wh3hj">
            <div className="h-px bg-gray-200 flex-1" data-id="element-909" data-mp="34xxvsg3hhl8e"></div>
            <span className="text-[10px] font-bold text-gray-400 uppercase px-2 tracking-wider" data-id="element-910" data-mp="30yyio87lmi5v">
              Requirements
            </span>
            <div className="h-px bg-gray-200 flex-1" data-id="element-911" data-mp="2w1gh52vwv57w"></div>
          </div>
          <div className="min-h-[90px] flex flex-col justify-center" data-id="element-912" data-mp="2r09ax89f2e3c">
            {requirements}
          </div>
        </div>

        {/* Focus */}
        <div className="mb-4" data-id="element-913" data-mp="p95z4fnsith7">
          <div className="flex items-center justify-center mb-3" data-id="element-914" data-mp="p7cut70nglcd">
            <div className="h-px bg-gray-200 flex-1" data-id="element-915" data-mp="k91k6u2j66t3"></div>
            <span className="text-[10px] font-bold text-gray-400 uppercase px-2 tracking-wider" data-id="element-916" data-mp="k7gm4uh0c5qh">
              Focus
            </span>
            <div className="h-px bg-gray-200 flex-1" data-id="element-917" data-mp="wrncwy2p8ufz"></div>
          </div>
          <ul className="space-y-2" data-id="element-918" data-mp="wpssdkvcb4cf">
            {focusItems.map((item, idx) => <li key={idx} className="flex items-start gap-2 text-xs text-gray-700 font-medium" data-id="element-919" data-mp="11kyn996pztcn" data-mpi={idx}>
                <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${colorClass}`} data-id="element-920" data-mp="9shfghufo3qz" />
                <span className="leading-snug" data-id="element-921" data-mp="duchxpo3on6c">{item}</span>
              </li>)}
          </ul>
        </div>

        {/* Goal Footer */}
        <div className="mt-4 flex items-start gap-3" data-id="element-922" data-mp="iuqm2wmkzcz3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${colorClass.replace('text-', 'bg-').replace('600', '100').replace('500', '100').replace('[#0a1e5e]', 'blue-100')} ${colorClass}`} data-id="element-923" data-mp="nsa0pxn6xrrk">
            <Star className="w-4 h-4" data-id="element-924" data-mp="383kpqembvoy0" />
          </div>
          <div className="min-w-0" data-id="element-925" data-mp="384dpgvmvde6n">
            <p className="text-[10px] font-bold text-gray-500 uppercase leading-tight mb-0.5" data-id="element-926" data-mp="385fbfv5fbl35">
              Goal:
            </p>
            <p className={`text-xs font-bold leading-tight ${colorClass} break-words`} data-id="element-927" data-mp="3h4sdi2r7x7du">
              {goal}
            </p>
            <p className="text-[10px] text-gray-600 font-medium leading-tight mt-0.5 break-words" data-id="element-928" data-mp="3c7accyjfzwge">
              {goalSub}
            </p>
          </div>
        </div>
      </div>
    </div>;
}